import React, { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { PageHeader, SectionCard, StatusBadge, ProvenanceBadge, Mono, InfoNote } from "@/components/uikit";
import { AnchorDialog } from "@/components/AnchorDialog";
import { SystemInfoPanel } from "@/components/SystemInfoPanel";
import LiveSourceProvenance from "@/components/LiveSourceProvenance";
import { ORG } from "@/lib/constants";

function MetricCard({ label, value, sub, icon, tone = "cyan", testId }) {
  const Icon = Icons[icon];
  const tones = {
    cyan: "text-cyan-400 border-cyan-900/60",
    emerald: "text-emerald-400 border-emerald-900/60",
    rose: "text-rose-400 border-rose-900/60",
    amber: "text-amber-400 border-amber-900/60",
    slate: "text-slate-300 border-slate-800",
  };
  return (
    <div data-testid={testId} className="animate-fade-up rounded-lg border border-slate-800 bg-[#0D121D] p-4 shadow-lg transition-colors hover:border-slate-700">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
        <span className={`flex h-8 w-8 items-center justify-center rounded-md border bg-slate-950 ${tones[tone]}`}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
      </div>
      <p className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-slate-100">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { stats, records, integrity } = useApp();
  const [anchorOpen, setAnchorOpen] = useState(false);
  const recent = [...records].slice(-5).reverse();

  return (
    <div>
      <PageHeader
        overline="Mission Assurance Console"
        title="Command Dashboard"
        icon="LayoutDashboard"
        subtitle="Independent, locally verifiable evidence layer for digital and AI-generated events. Capture → Anchor → Detect → Verify → Reconstruct."
        actions={
          <Button onClick={() => setAnchorOpen(true)} className="h-11 bg-cyan-500 px-5 font-bold text-slate-950 hover:bg-cyan-400 glow-cyan" data-testid="dashboard-anchor-button">
            <Icons.Anchor className="mr-2 h-4 w-4" aria-hidden="true" /> ANCHOR EVENT
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <MetricCard testId="metric-total-anchors" label="Total Anchors" value={stats.total} sub="Records in local chain" icon="Anchor" tone="cyan" />
        <MetricCard testId="metric-verified" label="Verified Records" value={stats.verified} sub="Hash + link intact" icon="ShieldCheck" tone="emerald" />
        <MetricCard testId="metric-failures" label="Integrity Failures" value={stats.failures} sub="Tamper / chain break" icon="ShieldAlert" tone={stats.failures ? "rose" : "slate"} />
        <MetricCard testId="metric-drift-alerts" label="Active Drift Alerts" value={stats.driftAlerts} sub="WARNING / DRIFT status" icon="Waves" tone={stats.driftAlerts ? "amber" : "slate"} />
        <MetricCard testId="metric-convergence" label="HCT-3 Convergence" value={`${stats.convergence}%`} sub="Latest simulator run" icon="Activity" tone="cyan" />
        <MetricCard testId="metric-chain-health" label="Chain Health" value={`${stats.chainHealth}%`} sub="Verified / total" icon="Link2" tone={stats.chainHealth === 100 ? "emerald" : "amber"} />
        <MetricCard testId="metric-latest-anchor" label="Latest Anchor" value={stats.latest ? stats.latest.payload_type : "—"} sub={stats.latest ? new Date(stats.latest.timestamp).toLocaleString() : "No records"} icon="Clock" tone="slate" />
        <MetricCard testId="metric-system-status" label="System Status" value={stats.systemStatus} sub={`${ORG.system} · CAGE ${ORG.cage}`} icon="ServerCog" tone={stats.systemStatus === "OPERATIONAL" ? "emerald" : "rose"} />
      </div>

      <div className="mb-6">
        <LiveSourceProvenance />
      </div>

      <div className="mb-6">
        <InfoNote tone="info">
          <strong>Evidence provenance is always labelled.</strong> This console produces{" "}
          <span className="text-cyan-300">LOCAL cryptographic evidence</span> and{" "}
          <span className="text-amber-300">SIMULATED demo telemetry</span>. It never claims blockchain, RFC 3161,
          government, or third-party verification unless a real external anchor is configured (none is).
        </InfoNote>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard title="Recent Anchors" icon="History" className="lg:col-span-2" testId="recent-anchors-card"
          right={<Link to="/vault" className="font-mono text-[11px] uppercase tracking-wider text-cyan-400 hover:text-cyan-300">View vault →</Link>}>
          {recent.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">No anchors yet. Use ANCHOR EVENT to create the first record.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Event</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Integrity</th>
                    <th className="py-2 pr-3">SHA-256</th>
                    <th className="py-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => {
                    const idx = records.findIndex((x) => x.id === r.id);
                    const res = integrity[idx];
                    const ok = res && res.selfOk && res.linkOk;
                    return (
                      <tr key={r.id} className="border-b border-slate-900 text-slate-300" data-testid={`recent-row-${r.id}`}>
                        <td className="py-2.5 pr-3"><Mono>{r.event_id}</Mono></td>
                        <td className="py-2.5 pr-3">{r.payload_type}</td>
                        <td className="py-2.5 pr-3"><StatusBadge status={ok ? "VERIFIED" : "WARNING"} /></td>
                        <td className="py-2.5 pr-3"><Mono className="max-w-[140px] truncate inline-block align-middle">{r.sha256}</Mono></td>
                        <td className="py-2.5"><ProvenanceBadge source={r.source_tag} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <SystemInfoPanel testId="system-info-card" />
      </div>

      <AnchorDialog open={anchorOpen} onOpenChange={setAnchorOpen} />
    </div>
  );
}
